﻿import { AttributeDefinition, AttributeType } from '@/shared/helpers/api/types/entities.ts';
import { ReferenceFilterNode, ReferenceFilterOperator } from '@/shared/codeedit/dynamicFilterTypes.ts';
import {
    builtInFields,
    builtInFieldType,
    isFilterableType,
    literalValueError,
    operatorIsAllowedForGroup,
    paramIsComparable,
    TypeGroup,
    typeGroup
} from '@/shared/codeedit/dynamicFilterRules.ts';

/*
Pure row/predicate logic for DynamicFilterBuilderModal.vue: converts between the modal's editable
`ClauseRow` rows and the `ReferenceFilterNode` predicate, and answers what a row may currently show
(available operators/params/value modes, literal input type, validation messages).
 */

export type ValueMode = 'param' | 'literal';

export type ClauseRow = {
    id: string;
    attribute: string;
    op: ReferenceFilterOperator | '';
    valueMode: ValueMode;
    literal: string | number | boolean;
    param: string;
    valueTouched: boolean;
    caseSensitive: boolean;
};

export type ClauseContext = {
    currentLibraryName: string;
    currentLibraryAttrDefs: AttributeDefinition[];
    refLibAttrDefs: AttributeDefinition[];
    // Whether each library is case-sensitive, by lower-case name
    libraryCaseSensitivity: ReadonlyMap<string, boolean>;
};

export function withLibraryPrefix(library: string, names: string[]): { value: string; label: string }[] {
    const prefix = library ? `${library}.` : '';
    return names.map(name => ({ value: name, label: `${prefix}${name}` }));
}

function findAttrDef(attrDefs: AttributeDefinition[], name: string): AttributeDefinition | undefined {
    return attrDefs.find(a => a.name.toLowerCase() === name.toLowerCase());
}

export function refFieldType(attributeName: string, ctx: ClauseContext): AttributeType | null {
    return builtInFieldType(attributeName) ?? findAttrDef(ctx.refLibAttrDefs, attributeName)?.attributeType ?? null;
}

function refFieldGroup(attributeName: string, ctx: ClauseContext): TypeGroup {
    return typeGroup(refFieldType(attributeName, ctx));
}

const allOperators: { value: ReferenceFilterOperator; label: string }[] = [
    { value: 'Eq', label: '=' },
    { value: 'Neq', label: '≠' },
    { value: 'Gt', label: '>' },
    { value: 'Lt', label: '<' },
    { value: 'Ge', label: '≥' },
    { value: 'Le', label: '≤' },
    { value: 'Contains', label: 'Contains' },
    { value: 'Prefix', label: 'Starts with' },
    { value: 'Suffix', label: 'Ends with' },
    { value: 'Exact', label: 'Equals' },
    { value: 'In', label: 'In (list)' }
];

// Exact compares the whole value like Eq, so it is kept only on a rule that already uses it
export function availableOperators(clause: ClauseRow, ctx: ClauseContext): { value: ReferenceFilterOperator; label: string }[] {
    const refGroup = refFieldGroup(clause.attribute, ctx);
    return allOperators.filter(o => operatorIsAllowedForGroup(o.value, refGroup) && (o.value !== 'Exact' || clause.op === 'Exact'));
}

export function availableParams(clause: ClauseRow, ctx: ClauseContext): { value: string; label: string }[] {
    const refGroup = refFieldGroup(clause.attribute, ctx);
    const names = [
        ...builtInFields.filter(name => paramIsComparable(builtInFieldType(name), refGroup)),
        ...ctx.currentLibraryAttrDefs
            .filter(a => isFilterableType(a.attributeType) && paramIsComparable(a.attributeType, refGroup))
            .map(a => a.name)
    ];
    return withLibraryPrefix(ctx.currentLibraryName, names);
}

const valuesWithoutCase: Record<'Numeric' | 'Temporal' | 'Bool', string> = { Numeric: 'Numbers', Temporal: 'Dates', Bool: 'True/false values' };

export type CaseSensitivityLock = { matchesCase: boolean; reason: string };

// How a rule that cannot choose matches case, and why, or null when it can choose. Only text rules can, as a
// CodeRef rule compares identities, which follow the case setting of the library they belong to. Mirrors
// ValidateComparison() in AttributeDefinitionCreateOrUpdateModel.cs.
export function caseSensitivityLock(clause: ClauseRow, ctx: ClauseContext): CaseSensitivityLock | null {
    const group = refFieldGroup(clause.attribute, ctx);
    // A rule without a field yet can be ticked; onAttributeChanged clears it if the field has no case
    if (group === 'Text' || group === 'Unknown') return null;
    if (group !== 'CodeRef') return { matchesCase: false, reason: `${valuesWithoutCase[group]} have no case, so this rule cannot be case-sensitive.` };

    const sides = [findAttrDef(ctx.refLibAttrDefs, clause.attribute)];
    if (clause.valueMode === 'param') sides.push(findAttrDef(ctx.currentLibraryAttrDefs, clause.param));
    if (sides.some(side => side?.attributeType === 'LibraryRef')) return { matchesCase: false, reason: 'Library names are matched ignoring case, so this rule cannot be case-sensitive.' };

    const libraries = [...new Set(sides.flatMap(side => side?.referenceLibraryName ?? []))];
    const isCaseSensitive = (library: string) => ctx.libraryCaseSensitivity.get(library.toLowerCase()) === true;
    const described = libraries.map(library => `${library} (${isCaseSensitive(library) ? '' : 'not '}case-sensitive)`);
    const matchesCase = libraries.every(isCaseSensitive);
    const outcome = matchesCase ? 'is case-sensitive' : 'is not case-sensitive';
    return { matchesCase, reason: `A CodeRef compares the identities of codes in ${described.join(' and ')}, so this rule ${outcome}.` };
}

// Whether a saved rule matches case, decided the same way as the box in the builder. A rule whose field
// is not among the reference library's definitions falls back to its own setting.
export function ruleMatchesCase(node: ReferenceFilterNode, ctx: ClauseContext): boolean {
    return caseSensitivityLock(clauseToRow(node), ctx)?.matchesCase ?? node.caseSensitive === true;
}

export function availableValueModes(op: ReferenceFilterOperator | ''): { value: ValueMode; label: string }[] {
    const modes: { value: ValueMode; label: string }[] = [
        { value: 'literal', label: 'Fixed value' }
    ];
    if (op !== 'In') modes.unshift({ value: 'param', label: 'Current form value' });
    return modes;
}

export function onAttributeChanged(clause: ClauseRow, ctx: ClauseContext) {
    if (!availableOperators(clause, ctx).some(o => o.value === clause.op)) clause.op = '';
    if (!availableParams(clause, ctx).some(p => p.value === clause.param)) clause.param = '';
    clause.literal = refFieldGroup(clause.attribute, ctx) === 'Bool' ? true : '';
    if (caseSensitivityLock(clause, ctx)) clause.caseSensitive = false;
}

export function onOpChanged(clause: ClauseRow) {
    const modes = availableValueModes(clause.op);
    if (!modes.some(m => m.value === clause.valueMode)) clause.valueMode = modes[0].value;
}

export function literalInputType(clause: ClauseRow, ctx: ClauseContext): 'number' | 'bool-select' | 'temporal' | 'text' {
    const group = refFieldGroup(clause.attribute, ctx);
    if (group === 'Numeric' && clause.op !== 'In') return 'number';
    if (group === 'Bool') return 'bool-select';
    if (group === 'Temporal') return 'temporal';
    return 'text';
}

function splitList(literal: string | number | boolean): string[] {
    return String(literal).split(',').map(s => s.trim()).filter(Boolean);
}

function literalValue(clause: ClauseRow): string {
    return String(clause.literal).trim();
}

// The values an In clause contributes to the predicate
function literalValues(clause: ClauseRow): string[] {
    return splitList(clause.literal);
}

// Whether the fixed value suits the reference field's type; empty string means valid.
function literalError(clause: ClauseRow, ctx: ClauseContext): string {
    if (clause.valueMode !== 'literal' || !clause.op) return '';
    const isIn = clause.op === 'In';
    return literalValueError(
        isIn ? null : literalValue(clause),
        isIn ? literalValues(clause) : null,
        refFieldGroup(clause.attribute, ctx),
        clause.op
    );
}

function valueIsMissing(clause: ClauseRow): boolean {
    if (clause.valueMode === 'param') return !clause.param;
    return clause.op === 'In' ? literalValues(clause).length === 0 : literalValue(clause) === '';
}

function valueError(clause: ClauseRow, ctx: ClauseContext): string {
    if (valueIsMissing(clause)) return 'Must have a value';
    return literalError(clause, ctx);
}

export function valueMessage(clause: ClauseRow, ctx: ClauseContext): string {
    if (valueIsMissing(clause) && !clause.valueTouched) return '';
    return valueError(clause, ctx);
}

export function clauseError(clause: ClauseRow, ctx: ClauseContext): string {
    if (!clause.attribute) return 'Must have a reference field';
    if (!clause.op) return 'Must have a condition';
    return valueError(clause, ctx);
}

export function filterHasErrors(clauses: ClauseRow[], ctx: ClauseContext): boolean {
    return clauses.some(c => clauseError(c, ctx) !== '');
}

export function newClause(): ClauseRow {
    return { id: crypto.randomUUID(), attribute: '', op: '', valueMode: 'param', literal: '', param: '', valueTouched: false, caseSensitive: false };
}

export function buildPredicate(clauses: ClauseRow[], logicalOp: 'And' | 'Or', ctx: ClauseContext): ReferenceFilterNode {
    const nodes: ReferenceFilterNode[] = clauses.map(c => {
        const node: ReferenceFilterNode = {
            op: c.op as ReferenceFilterOperator,
            attribute: c.attribute,
            group: refFieldGroup(c.attribute, ctx)
        };
        if (c.valueMode === 'param') {
            node.param = c.param;
        } else if (c.op === 'In') {
            node.values = literalValues(c);
        } else {
            node.value = literalValue(c);
        }
        if (c.caseSensitive && refFieldGroup(c.attribute, ctx) === 'Text') {
            node.caseSensitive = true;
        }
        return node;
    });

    if (nodes.length === 1) {
        return nodes[0];
    }
    return { op: logicalOp, clauses: nodes };
}

function clauseToRow(node: ReferenceFilterNode): ClauseRow {
    const desiredMode: ClauseRow['valueMode'] = node.param ? 'param' : 'literal';
    const row: ClauseRow = {
        id: crypto.randomUUID(),
        attribute: node.attribute ?? '',
        op: node.op,
        valueMode: availableValueModes(node.op).some(m => m.value === desiredMode)
            ? desiredMode
            : 'literal',
        literal: '',
        param: node.param ?? '',
        valueTouched: true,
        caseSensitive: node.caseSensitive === true
    };
    if (node.values && node.values.length > 0) {
        row.literal = node.values.join(', ');
    } else if (node.value !== undefined && node.value !== null) {
        row.literal = node.value;
    }
    return row;
}

export function parseExistingPredicate(node: ReferenceFilterNode | null): { logicalOp: 'And' | 'Or'; clauses: ClauseRow[] } | null {
    if (!node) return null;
    if (node.op === 'And' || node.op === 'Or') {
        return { logicalOp: node.op, clauses: (node.clauses ?? []).map(clauseToRow) };
    }
    return { logicalOp: 'And', clauses: [clauseToRow(node)] };
}
